import React from 'react';
import Table from '../../components/common/Table';
import { EmailMessageThreadDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import Badge from '../common/Badge';
import { getBadgeColors } from '../../utils/displayUtils';
import createEmailMessageThreadActions from '../actionMenus/EmailMessageThreadActions';

interface EmailMessageThreadTableProps {
  data: EmailMessageThreadDTO[];
  handleEdit: (row: EmailMessageThreadDTO) => void;
  handleDelete: (row: EmailMessageThreadDTO) => Promise<void>;
  handleMarkAsReplied?: (row: EmailMessageThreadDTO) => Promise<void>;
  handleMarkAsResolved?: (row: EmailMessageThreadDTO) => Promise<void>;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const EmailMessageThreadTable: React.FC<EmailMessageThreadTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  handleMarkAsReplied,
  handleMarkAsResolved,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  return (
    <Table
      columns={[
        {
          label: 'Subject',
          key: 'subject',
        },

        {
          label: 'Component',
          key: 'componentId',
          render: (row: EmailMessageThreadDTO) => (
            <div className="flex flex-col gap-1">
              <span>{row.component?.title}</span>
            </div>
          ),
        },

        {
          label: 'Member',
          key: 'memberId',
          render: (row: EmailMessageThreadDTO) => (
            <div className="flex flex-col gap-1">
              <span>
                {row.rootEmailMessage?.member?.firstName} {row.rootEmailMessage?.member?.lastName}
              </span>
            </div>
          ),
        },

        {
          label: 'Id number',
          key: 'idNumber',
          render: (row: EmailMessageThreadDTO) => (
            <div className="flex flex-col gap-1">
              <span>{row.rootEmailMessage?.member?.idPassportNumber ?? ''}</span>
            </div>
          ),
        },

        {
          label: 'Admin',
          key: 'adminUserId',
          render: (row: EmailMessageThreadDTO) => (
            <div className="flex flex-col gap-1">
              <span>
                {row.rootEmailMessage?.adminUser?.firstName}{' '}
                {row.rootEmailMessage?.adminUser?.lastName}
              </span>
            </div>
          ),
        },

        {
          label: 'Status',
          key: 'replyTypeId',
          render: (row: EmailMessageThreadDTO) => {
            if (row.isDeleted) {
              return <Badge label="Deleted" variant="error" />;
            }
            const base = row.lastEmailMessage?.replyType?.colourGrading?.colour ?? '#ffffffff';
            const { bg, text } = getBadgeColors(base, 0.18, 0.4);
            return base ? (
              <Badge
                label={row.lastEmailMessage?.replyType?.title || 'Unknown'}
                variant="custom"
                customBgColor={bg}
                customTextColor={text}
              />
            ) : (
              <span>{row.lastEmailMessage?.replyType?.title || 'Unknown'}</span>
            );
          },
        },

        {
          label: 'Updated',
          key: 'updatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },

        {
          label: 'Added',
          key: 'createdUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createEmailMessageThreadActions({
            onDelete: handleDelete,
            onMarkAsReplied: handleMarkAsReplied,
            onMarkAsResolved: handleMarkAsResolved,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default EmailMessageThreadTable;
